import React from 'react';
import { Offcanvas, Button, Form } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import DateFilter from '../../../Receipts/components/Filters/DateFilter';
import './OffCanvas.scss';

type Section = 'general' | 'productos' | 'frecuencia';
type TimeRange = 'current' | '7d' | '30d' | 'custom';
type AggregationType = 'by_item' | 'by_user' | 'by_pedido_type' | 'by_menu_category';

interface VentasOffCanvasProps {
  show: boolean;
  onHide: () => void;
  section: Section;
  
  // Date filters
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  
  // Amount range filters
  enableAmountRange: boolean;
  amountRange: [number, number];
  onEnableAmountRangeChange: (enabled: boolean) => void;
  onAmountRangeChange: (range: [number, number]) => void;
  
  // ProductStats filters (only for productos section)
  timeRange?: TimeRange;
  aggregationType?: AggregationType;
  onTimeRangeChange?: (timeRange: TimeRange) => void;
  onAggregationTypeChange?: (aggregationType: AggregationType) => void;
}

const VentasOffCanvas: React.FC<VentasOffCanvasProps> = ({
  show,
  onHide,
  section,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  enableAmountRange,
  amountRange,
  onEnableAmountRangeChange,
  onAmountRangeChange,
  timeRange = 'current',
  aggregationType = 'by_item',
  onTimeRangeChange,
  onAggregationTypeChange
}) => {

  // Helper functions for labels
  const getTimeRangeLabel = (range: TimeRange) => {
    switch (range) {
      case 'current': return 'Última sesión';
      case '7d': return '7 días';
      case '30d': return '30 días';
      case 'custom': return 'Personalizado';
      default: return range;
    }
  };

  const getAggregationTypeLabel = (type: AggregationType) => {
    switch (type) {
      case 'by_item': return 'Por producto';
      case 'by_menu_category': return 'Por categoría';
      case 'by_user': return 'Por usuario';
      case 'by_pedido_type': return 'Por canal';
      default: return type;
    }
  };

  // Render ProductStats specific filters
  const renderProductStatsFilters = () => {
    return (
      <div className="filter-section">
        <h6 className="filter-section-title">Análisis de Productos</h6>
        
        <div className="filter-row-horizontal">
          {/* Time Range Filter */}
          <div className="filter-group compact">
            <Form.Label className="filter-label">Período</Form.Label>
            <div className="button-group-vertical">
              {(['current', '7d', '30d'] as TimeRange[]).map((range) => (
                <Button
                  key={range}
                  variant="none"
                  className={`filter-button ${timeRange === range ? 'active' : ''}`}
                  onClick={() => onTimeRangeChange?.(range)}
                >
                  {getTimeRangeLabel(range)}
                </Button>
              ))}
            </div>
          </div>

          {/* Aggregation Type Filter */}
          <div className="filter-group compact">
            <Form.Label className="filter-label">Vista</Form.Label>
            <div className="button-group-vertical">
              {(['by_item', 'by_menu_category', 'by_user', 'by_pedido_type'] as AggregationType[]).map((type) => (
                <Button
                  key={type}
                  variant="none"
                  className={`filter-button ${aggregationType === type ? 'active' : ''}`}
                  onClick={() => onAggregationTypeChange?.(type)}
                >
                  {getAggregationTypeLabel(type)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render general filters (dates and amount)
  const renderGeneralFilters = () => (
    <div className="filter-section">
      <h6 className="filter-section-title">Filtros Generales</h6>
      
      {/* Date Range */}
      <div className="filter-group">
        <Form.Label className="filter-label">Rango de fechas</Form.Label>
        <DateFilter
          startDate={startDate}
          setStartDate={onStartDateChange}
          endDate={endDate}
          setEndDate={onEndDateChange}
        />
      </div>

    </div>
  );

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="mp-offcanvas ventas-offcanvas"
      backdrop={true}
      scroll={false}
      style={{ zIndex: 9999 }}
    >
      <Offcanvas.Header className="offcanvas-header">
        <div className="header-content">
          <Offcanvas.Title className="offcanvas-title">
            Opciones de Filtrado
          </Offcanvas.Title>
          <div className="section-indicator">
            {section === 'general' ? 'Ventas Generales' : 'Estadísticas de Productos'}
          </div>
        </div>
        <Button
          variant="none"
          className="close-button"
          onClick={onHide}
        >
          <X size={24} />
        </Button>
      </Offcanvas.Header>
      
      <Offcanvas.Body className="offcanvas-body">
        <div className="filters-container">
          {section === 'productos' && renderProductStatsFilters()}
          {renderGeneralFilters()}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default VentasOffCanvas;
